import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Car, Loader2, X, ArrowUpRight, Zap, Fuel } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "EV", "CNG+Petrol"];
const POWERTRAINS = ["Petrol", "Diesel", "CNG", "Hybrid", "PHEV", "Electric"];
// Reasonable default powertrain per fuel_type
const powertrainForFuel = (f) => {
  if (f === "EV") return "Electric";
  if (f === "CNG+Petrol") return "CNG";
  return f || "Petrol";
};

const emptyForm = {
  number: "", name: "", fuel_type: "Petrol", powertrain: "Petrol",
  initial_odometer: 0, service_interval_km: 10000, service_interval_months: 6,
  purchase_price: "", purchase_date: "", insurance_annual: "",
  depreciation_pct_annual: 15, battery_capacity_kwh: "",
};

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = !!(user && user.is_admin);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        initial_odometer: Number(form.initial_odometer),
        service_interval_km: Number(form.service_interval_km) || 10000,
        service_interval_months: Number(form.service_interval_months) || 6,
        purchase_price: form.purchase_price ? Number(form.purchase_price) : null,
        purchase_date: form.purchase_date || null,
        insurance_annual: form.insurance_annual ? Number(form.insurance_annual) : null,
        depreciation_pct_annual: Number(form.depreciation_pct_annual) || 15,
        battery_capacity_kwh: form.battery_capacity_kwh ? Number(form.battery_capacity_kwh) : null,
      };
      await api.post("/vehicles", payload);
      toast.success("Vehicle added");
      setForm(emptyForm); setShowForm(false); load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.number}? This also removes its refuel history.`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehicle removed");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
  };

  const isEV = form.powertrain === "Electric" || form.powertrain === "PHEV";

  return (
    <div className="space-y-8" data-testid="garage-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="ft-overline mb-2">GARAGE</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Your Vehicles</h1>
          <p className="mt-3 text-[color:var(--text-secondary,#A3A3A3)]">
            {isAdmin ? "Add each car, bike or truck once — every future refuel or charge picks from this list." : "Read-only roster. Ask an admin to add or edit vehicles."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-3 font-bold inline-flex items-center gap-2 transition-colors duration-200"
            style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }}
            data-testid="add-vehicle-button"
          >
            {showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? "Cancel" : "Add Vehicle"}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={submit} className="ft-card p-6 grid grid-cols-1 md:grid-cols-4 gap-4 ft-fade-up" data-testid="vehicle-form">
          <div>
            <label className="ft-overline block mb-2">Number Plate</label>
            <input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value.toUpperCase() })} placeholder="KA05MJ1234" className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-number-input" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Name / Model</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Honda City" className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-name-input" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Fuel Type</label>
            <select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value, powertrain: powertrainForFuel(e.target.value) })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-fueltype-select">
              {FUEL_TYPES.map((f) => (<option key={f}>{f}</option>))}
            </select>
          </div>
          <div>
            <label className="ft-overline block mb-2">Powertrain</label>
            <select value={form.powertrain} onChange={(e) => setForm({ ...form, powertrain: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-powertrain-select">
              {POWERTRAINS.map((f) => (<option key={f}>{f}</option>))}
            </select>
          </div>
          <div>
            <label className="ft-overline block mb-2">Initial Odometer (km)</label>
            <input type="number" step="0.01" value={form.initial_odometer} onChange={(e) => setForm({ ...form, initial_odometer: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-odo-input" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Service interval (km)</label>
            <input type="number" value={form.service_interval_km} onChange={(e) => setForm({ ...form, service_interval_km: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-interval-km" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Service interval (months)</label>
            <input type="number" value={form.service_interval_months} onChange={(e) => setForm({ ...form, service_interval_months: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-interval-months" />
          </div>
          {isEV && (
            <div>
              <label className="ft-overline block mb-2">Battery (kWh)</label>
              <input type="number" step="0.1" value={form.battery_capacity_kwh} onChange={(e) => setForm({ ...form, battery_capacity_kwh: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-battery-kwh" />
            </div>
          )}

          <div className="md:col-span-4 pt-2 border-t border-[color:var(--border-line,#262626)]">
            <p className="ft-overline text-xs">OWNERSHIP (optional — enables full TCO)</p>
          </div>
          <div>
            <label className="ft-overline block mb-2">Purchase price (₹)</label>
            <input type="number" step="1" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-purchase-price" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Purchase date</label>
            <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-purchase-date" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Insurance premium / year (₹)</label>
            <input type="number" step="1" value={form.insurance_annual} onChange={(e) => setForm({ ...form, insurance_annual: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-insurance-annual" />
          </div>
          <div>
            <label className="ft-overline block mb-2">Depreciation % / year</label>
            <input type="number" step="0.5" value={form.depreciation_pct_annual} onChange={(e) => setForm({ ...form, depreciation_pct_annual: e.target.value })} className="w-full bg-[color:var(--bg-base,#0A0A0A)] border border-[color:var(--border-line,#262626)] px-4 py-3 font-mono focus:outline-none focus:border-[color:var(--accent,#FF5A1F)]" data-testid="vehicle-depreciation" />
          </div>

          <div className="md:col-span-4 flex justify-end">
            <button type="submit" disabled={saving} className="px-6 py-3 font-bold inline-flex items-center gap-2 transition-colors disabled:opacity-60" style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }} data-testid="vehicle-submit-button">
              {saving && <Loader2 size={16} className="animate-spin" />} Save Vehicle
            </button>
          </div>
        </form>
      )}

      <div className="ft-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[color:var(--text-tertiary,#737373)]">Loading…</div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Car className="mx-auto text-[color:var(--text-tertiary,#404040)]" size={40} />
            <p className="mt-4 text-[color:var(--text-secondary,#A3A3A3)]">No vehicles yet. {isAdmin ? "Add your first vehicle to start tracking." : "Ask an admin to add a vehicle."}</p>
          </div>
        ) : (
          <table className="w-full" data-testid="vehicles-table">
            <thead>
              <tr>
                <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-tertiary,#737373)] border-b border-[color:var(--border-line,#262626)]">Number</th>
                <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-tertiary,#737373)] border-b border-[color:var(--border-line,#262626)]">Name</th>
                <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-tertiary,#737373)] border-b border-[color:var(--border-line,#262626)]">Powertrain</th>
                <th className="text-right py-4 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-tertiary,#737373)] border-b border-[color:var(--border-line,#262626)]">Initial Odo</th>
                <th className="text-right py-4 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-tertiary,#737373)] border-b border-[color:var(--border-line,#262626)]">Actions</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {vehicles.map((v) => {
                const pt = v.powertrain || v.fuel_type;
                const ev = ["Electric", "PHEV", "Hybrid", "EV"].includes(pt);
                return (
                  <tr key={v.id} className="border-b border-[color:var(--border-line,#262626)] hover:bg-[color:var(--bg-elevated,#1A1A1A)] transition-colors" data-testid={`vehicle-row-${v.id}`}>
                    <td className="py-4 px-4 font-bold">{v.number}</td>
                    <td className="py-4 px-4 text-[color:var(--text-secondary,#A3A3A3)]">{v.name}</td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 text-xs font-bold uppercase tracking-wider border border-[color:var(--accent,#FF5A1F)]/40 text-[color:var(--accent,#FF5A1F)] bg-[color:var(--accent,#FF5A1F)]/10 inline-flex items-center gap-1">
                        {ev ? <Zap size={10} /> : <Fuel size={10} />} {pt}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right text-[color:var(--text-secondary,#A3A3A3)]">{v.initial_odometer?.toLocaleString?.() ?? v.initial_odometer} km</td>
                    <td className="py-4 px-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/vehicles/${v.id}`}
                          className="text-[color:var(--accent,#FF5A1F)] hover:text-white hover:bg-[color:var(--accent,#FF5A1F)] px-3 py-1.5 transition-colors inline-flex items-center gap-2 border border-[color:var(--accent,#FF5A1F)]/40"
                          data-testid={`vehicle-view-${v.id}`}
                        >
                          <ArrowUpRight size={14} /> View
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => remove(v)}
                            className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] px-3 py-1.5 transition-colors inline-flex items-center gap-2"
                            data-testid={`vehicle-delete-${v.id}`}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
